import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { apiService } from '../services/apiService';

const ChannelKPIs = ({ showSnackbar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [countries, setCountries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [countryChannels, setCountryChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingKpi, setEditingKpi] = useState(null);
  const [formData, setFormData] = useState({
    country_code: '',
    channel_id: '',
    month: '',
    reach: '',
    impressions: '',
    engagement: '',
    profile_visits: ''
  });
  const [savingKPI, setSavingKPI] = useState(false);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- cargar solo al montar
  }, []);

  useEffect(() => {
    if (selectedCountry !== 'all' || selectedChannel !== 'all' || selectedMonth) {
      loadKPIs();
    } else {
      loadKPIs(); // Cargar todos si no hay filtros
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recargar al cambiar filtros
  }, [selectedCountry, selectedChannel, selectedMonth]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [countriesRes, channelsRes, countryChannelsRes] = await Promise.all([
        apiService.getCountries({ for_kpis: true }),
        apiService.getChannels(),
        apiService.getCountryChannels()
      ]);

      if (countriesRes.success) setCountries(countriesRes.countries);
      if (channelsRes.success) {
        const allChannels = channelsRes.channels || [];
        setChannels(allChannels.filter(ch => ['CH001', 'CH002'].includes(ch.id)));
      }
      if (countryChannelsRes.success) setCountryChannels(countryChannelsRes.country_channels);
      
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los datos');
      setLoading(false);
    }
  };

  const loadKPIs = async () => {
    try {
      const params = {};
      if (selectedCountry !== 'all') params.country_code = selectedCountry;
      if (selectedChannel !== 'all') params.channel_id = selectedChannel;
      if (selectedMonth) {
        // Convertir mes YYYY-MM a fecha del primer día
        const monthDate = new Date(selectedMonth + '-01');
        params.month = monthDate.toISOString().split('T')[0];
      }

      const response = await apiService.getChannelKPIs(params);
      if (response.success) {
        setKpis(response.kpis);
      }
    } catch (err) {
      showSnackbar('Error al cargar KPIs', 'error');
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('es-ES').format(num);
  };

  const handleOpenDialog = (kpi = null) => {
    if (kpi) {
      setEditingKpi(kpi);
      setFormData({
        country_code: kpi.country_code,
        channel_id: kpi.channel_id,
        month: kpi.month || '',
        reach: kpi.reach || '',
        impressions: kpi.impressions || '',
        engagement: kpi.engagement || '',
        profile_visits: kpi.profile_visits || ''
      });
    } else {
      setEditingKpi(null);
      setFormData({
        country_code: selectedCountry !== 'all' ? selectedCountry : '',
        channel_id: selectedChannel !== 'all' ? selectedChannel : '',
        month: selectedMonth || '',
        reach: '',
        impressions: '',
        engagement: '',
        profile_visits: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingKpi(null);
    setFormData({
      country_code: '',
      channel_id: '',
      month: '',
      reach: '',
      impressions: '',
      engagement: '',
      profile_visits: ''
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.country_code || !formData.channel_id || !formData.month) {
        showSnackbar('País, canal y mes son requeridos', 'error');
        return;
      }

      setSavingKPI(true);

      // Asegurar que el mes sea el primer día del mes
      const monthDate = new Date(formData.month);
      const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthString = firstDayOfMonth.toISOString().split('T')[0];

      const kpiData = {
        country_code: formData.country_code,
        channel_id: formData.channel_id,
        month: monthString,
        reach: formData.reach ? parseFloat(formData.reach) : null,
        impressions: formData.impressions ? parseFloat(formData.impressions) : null,
        engagement: formData.engagement ? parseFloat(formData.engagement) : null,
        profile_visits: formData.profile_visits ? parseFloat(formData.profile_visits) : null
      };

      const response = await apiService.updateChannelKPIs(kpiData);
      if (response.success) {
        showSnackbar('KPIs guardados correctamente');
        handleCloseDialog();
        loadKPIs();
      }
    } catch (err) {
      showSnackbar('Error al guardar KPIs', 'error');
    } finally {
      setSavingKPI(false);
    }
  };

  // Obtener canales activos para un país
  const getChannelsForCountry = (countryCode) => {
    if (countryCode === 'all') return channels;
    const activeChannels = countryChannels
      .filter(cc => cc.country_code === countryCode && cc.is_active)
      .map(cc => cc.channel_id);
    return channels.filter(ch => activeChannels.includes(ch.id));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box 
        display="flex" 
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between" 
        alignItems={isMobile ? 'flex-start' : 'center'} 
        sx={{ mb: { xs: 2, sm: 3 }, gap: { xs: 2, sm: 0 } }}
      >
        <Typography variant={isMobile ? 'h5' : 'h4'}>
          KPIs por Canal
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          fullWidth={isMobile}
          size={isMobile ? 'medium' : 'large'}
        >
          Agregar KPIs
        </Button>
      </Box>

      {/* Selector de mes prominente */}
      <Card sx={{ mb: { xs: 2, sm: 3 }, backgroundColor: 'primary.main', color: 'white' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box 
            display="flex" 
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'} 
            gap={2}
          >
            <CalendarTodayIcon sx={{ fontSize: { xs: 32, sm: 40 } }} />
            <Box flexGrow={1} width={isMobile ? '100%' : 'auto'}>
              <Typography variant={isMobile ? 'subtitle1' : 'h5'} gutterBottom>
                {selectedMonth ? new Date(selectedMonth + '-01').toLocaleDateString('es-ES', { year: 'numeric', month: 'long' }) : 'Selecciona un mes'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Ingresa los KPIs mensuales para seguimiento
              </Typography>
            </Box>
            <TextField
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth={isMobile}
              sx={{
                backgroundColor: 'white',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'transparent'
                  }
                }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por País</InputLabel>
            <Select
              value={selectedCountry}
              label="Filtrar por País"
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              <MenuItem value="all">Todos los países</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Filtrar por Canal</InputLabel>
            <Select
              value={selectedChannel}
              label="Filtrar por Canal"
              onChange={(e) => setSelectedChannel(e.target.value)}
            >
              <MenuItem value="all">Todos los canales</MenuItem>
              {getChannelsForCountry(selectedCountry).map((channel) => (
                <MenuItem key={channel.id} value={channel.id}>
                  {channel.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Tabla de KPIs */}
      <TableContainer 
        component={Paper}
        sx={{ 
          maxWidth: '100%',
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: 650
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>País</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Canal</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Mes</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Reach</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Impresiones</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Engagement</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Visitas Perfil</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {kpis.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No hay KPIs registrados. Haz clic en "Agregar KPIs" para comenzar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              kpis.map((kpi, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {kpi.country_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {kpi.country_code}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{kpi.channel_name}</TableCell>
                  <TableCell>
                    {kpi.month ? new Date(kpi.month).toLocaleDateString('es-ES', { year: 'numeric', month: 'long' }) : 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {formatNumber(kpi.reach)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {formatNumber(kpi.impressions)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {formatNumber(kpi.engagement)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {formatNumber(kpi.profile_visits)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleOpenDialog(kpi)}
                    >
                      Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo para agregar/editar KPIs */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        fullScreen={isMobile}
        maxWidth={isMobile ? 'xs' : 'sm'} 
        fullWidth
      >
        <DialogTitle>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            {editingKpi ? 'Editar KPIs' : 'Agregar KPIs'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>País</InputLabel>
              <Select
                value={formData.country_code}
                label="País"
                onChange={(e) => setFormData({...formData, country_code: e.target.value})}
                required
              >
                {countries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Canal</InputLabel>
              <Select
                value={formData.channel_id}
                label="Canal"
                onChange={(e) => setFormData({...formData, channel_id: e.target.value})}
                required
              >
                {getChannelsForCountry(formData.country_code || selectedCountry).map((channel) => (
                  <MenuItem key={channel.id} value={channel.id}>
                    {channel.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Mes"
              type="month"
              value={formData.month}
              onChange={(e) => setFormData({...formData, month: e.target.value})}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              label="Reach"
              type="number"
              value={formData.reach}
              onChange={(e) => setFormData({...formData, reach: e.target.value})}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: 1 }}
              placeholder="Ej: 150000"
              helperText="Número de personas alcanzadas"
              InputProps={{
                endAdornment: <InputAdornment position="end">personas</InputAdornment>
              }}
            />
            <TextField
              fullWidth
              label="Impresiones"
              type="number"
              value={formData.impressions}
              onChange={(e) => setFormData({...formData, impressions: e.target.value})}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: 1 }}
              placeholder="Ej: 500000"
              helperText="Número de veces mostrado"
              InputProps={{
                endAdornment: <InputAdornment position="end">veces</InputAdornment>
              }}
            />
            <TextField
              fullWidth
              label="Engagement"
              type="number"
              value={formData.engagement}
              onChange={(e) => setFormData({...formData, engagement: e.target.value})}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: 1 }}
              placeholder="Ej: 12000"
              helperText="Número de interacciones"
              InputProps={{
                endAdornment: <InputAdornment position="end">interacciones</InputAdornment>
              }}
            />
            <TextField
              fullWidth
              label="Visitas al Perfil"
              type="number"
              value={formData.profile_visits}
              onChange={(e) => setFormData({...formData, profile_visits: e.target.value})}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: 1 }}
              placeholder="Ej: 3500"
              helperText="Número de visitas al perfil"
              InputProps={{
                endAdornment: <InputAdornment position="end">visitas</InputAdornment>
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.country_code || !formData.channel_id || !formData.month || savingKPI}
          >
            {savingKPI ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            {editingKpi ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChannelKPIs;
